import React, { useCallback } from 'react'

import { isNullOrUndefined, selectMostRecentUpdatedTimestamp } from '../utils'

import ResultFooter from './components/ResultFooter'
import ResultText from './components/ResultText'
import ResultTitle from './components/ResultTitle'

/**
 * Component for displaying the document results.
 *
 * @see {@link https://github.com/aws-samples/aws-kendra-transcribe-media-search/blob/main/finderapp/src/search/resultsPanel/DocumentResults.tsx}
 * @example
 * const indexId = "index-123"
 * const queryText = "sample query"
 * const results = [{ ... }]
 * const submitFeedback = (feedbackType, queryResultItem) => { ... }
 * return <DocumentResults indexId={indexId} queryText={queryText} results={results} submitFeedback={submitFeedback} />
 *
 * @param {Object} props - The component props.
 * @param {string} props.indexId - The index ID.
 * @param {string} props.queryText - The query text.
 * @param {Array} props.results - The array of result items.
 * @param {Function} props.submitFeedback - The function to submit feedback.
 * @returns {JSX.Element|null} The rendered DocumentResults component or null if no results are available.
 */
const DocumentResults = ({ indexId, queryText, results, submitFeedback }) => {
  /**
   * Renders individual result items.
   * All results in this component has QueryResultType === "ANSWER"
   *
   * @param {Object} result - The result item to be rendered.
   * @returns {JSX.Element} The rendered result item.
   */
  const renderResults = useCallback(
    result => {
      let attributes = {}
      if (!isNullOrUndefined(result.DocumentAttributes)) {
        result.DocumentAttributes.forEach(attribute => {
          attributes[attribute.Key] = attribute.Value
        })
      }

      const lastUpdated = selectMostRecentUpdatedTimestamp(attributes)

      return (
        <div className="container-body" key={result.Id}>
          <ResultTitle queryResultItem={result} />
          <ResultText
            className="small-margin-bottom"
            text={result.DocumentExcerpt}
            attributes={attributes}
            lastUpdated={lastUpdated}
          />
          <ResultFooter
            indexId={indexId}
            queryText={queryText}
            queryResultItem={result}
            attributes={attributes}
            submitFeedback={submitFeedback}
          />
        </div>
      )
    },
    [submitFeedback, indexId, queryText]
  )

  if (isNullOrUndefined(results) || results.length === 0) {
    return null
  }

  return (
    <div className="document-results-section">{results.map(renderResults)}</div>
  )
}

export default DocumentResults
